// src/components/UpdateProfileModal.tsx
import { memo, useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  FileInput,
  Button,
  Group,
  Avatar,
  Loader,
  LoadingOverlay,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { toast } from "react-toastify";
import { useAtom } from "jotai";
import { meAtom } from "../store/jotaiStore";
import authService from "../services/authService";
import { getErrorMessage } from "../utils/generateErrorMessage";
import fileService from "../services/fileService";

export interface UpdateProfileModalProps {
  visible: boolean;
  onClose: () => void;
}

const UpdateProfileModal = ({ visible, onClose }: UpdateProfileModalProps) => {
  const form = useForm({
    initialValues: {
      name: "",
      lastname: "",
      imageUrl: "",
    },
    validate: {
      name: (value) => (value.trim().length > 0 ? null : "Ad zorunlu"),
      lastname: (value) => (value.trim().length > 0 ? null : "Soyad zorunlu"),
      imageUrl: (value) => (value ? null : "Profil fotoğrafı yükleyin"),
    },
  });

  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [me, setMe] = useAtom(meAtom);

  useEffect(() => {
    if (!visible) {
      // modal kapanınca bir kez temizle
      form.reset();
      setUploading(false);
      return;
    }

    // modal açılınca me’den formu doldur, ama sadece fark varsa yaz
    const next = {
      name: me?.name ?? "",
      lastname: me?.lastname ?? "",
      imageUrl: me?.imageUrl ?? "",
    };

    const curr = form.values;
    const changed =
      curr.name !== next.name ||
      curr.lastname !== next.lastname ||
      curr.imageUrl !== next.imageUrl;

    if (changed) {
      form.setValues(next); // 3 kez tek tek yerine tek seferde
    }
    // DİKKAT: dependency’de form veya me objesinin kendisi yok
  }, [visible, me?.name, me?.lastname, me?.imageUrl]);

  const handleSubmit = async (values: typeof form.values) => {
    if (form.validate().hasErrors) {
      toast.error("Lütfen gerekli alanları doldurunuz.", {
        toastId: "change-profile-error",
      });
      return;
    }
    setLoading(true);
    await authService
      .updateUserProfile(values)
      .then(async () => {
        toast.success("Profil Güncellendi", {
          toastId: "change-profile",
        });
        await authService.me().then((res) => {
          setMe(res);
        });
      })
      .catch((err) => {
        toast.error(getErrorMessage(err.status), {
          toastId: "change-profile-error",
          position: "top-right",
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
        onClose();
      });

    onClose();
  };

  const handleFileChange = async (file: File | null) => {
    if (!file) return;
    try {
      setUploading(true);
      const url = await fileService.uploadProfilePhoto(file);
      form.setFieldValue("imageUrl", url);
    } catch (err: any) {
      console.error("Profile upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      opened={visible}
      onClose={onClose}
      title="Profil Güncelle"
      size="sm"
      centered
      zIndex={100}
    >
      <LoadingOverlay
        visible={loading}
        opacity={0}
        zIndex={100}
        overlayProps={{ radius: "sm", blur: 2 }}
      />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          label="İsim"
          required
          id="register-name"
          size="md"
          className="w-full"
          maxLength={20}
          minLength={2}
          {...form.getInputProps("name")}
        />

        <TextInput
          label="Soy isim"
          required
          id="register-surname"
          size="md"
          maxLength={20}
          minLength={2}
          className="w-full"
          {...form.getInputProps("lastname")}
        />

        <FileInput
          label="Profil Fotoğrafı"
          placeholder="Dosya seç ve yükle"
          accept="image/*"
          required
          value={null}
          onChange={handleFileChange}
          rightSection={uploading ? <Loader size="sm" /> : null}
        />

        {form.values.imageUrl && (
          <Group mt="md">
            <Avatar
              src={form.values.imageUrl}
              alt="Profil"
              radius="xl"
              size="lg"
            />
          </Group>
        )}

        <Group mt="md">
          <Button
            aria-label="İptal"
            variant="default"
            onClick={onClose}
            disabled={uploading}
          >
            İptal
          </Button>
          <Button aria-label="Kaydet" type="submit" loading={uploading}>
            Kaydet
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default memo(UpdateProfileModal);
